/**
 * Date contract for planner tools.
 *
 * Lifted from `WebMCP-org/big-calendar`
 * `src/calendar/components/calendar-webmcp-tools.tsx:61-98`, which is the
 * closest public analog to our calendar view. The SEMANTICS are copied
 * verbatim; two things are deliberately not:
 *
 * 1. **Values, not throws.** big-calendar throws `Error` on a bad date. Core's
 *    convention is that refusals are values and exceptions are for bugs, so
 *    every helper here returns `T | Refusal`. A refusal names the expected
 *    format AND gives one example, because the model gets no schema
 *    enforcement from Chrome — the runtime validates, nothing else does.
 * 2. **No date library.** big-calendar uses `date-fns`, which this frontend
 *    does not depend on; `dayjs` is only present transitively through antd and
 *    is not in `package.json`. Adding a dependency to a shared file that three
 *    lanes branch from is not worth it for four helpers, so this is native
 *    `Date`.
 *
 * The semantics that must not drift:
 * - `"today"` is a first-class literal, not a separate tool. It is never a
 *   DEFAULT: an absent window means no date filter at all.
 * - Month filters are `YYYY-MM`, expanded to an INCLUSIVE end at 23:59:59.999.
 * - Ranges match by OVERLAP, not containment.
 * - Start-before-end is checked AFTER merging a partial update, so moving only
 *   `from` is still validated against the stored `to`.
 */

import type { Refusal } from './contract';

export interface DateRange {
  readonly start: Date;
  readonly end: Date;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

function refuse(because: string): Refusal {
  return { kind: 'refused', because, retryable: true };
}

export function isRefusal(v: unknown): v is Refusal {
  return typeof v === 'object' && v !== null && (v as Refusal).kind === 'refused';
}

/**
 * Accepts a full ISO 8601 instant or a bare `YYYY-MM-DD`.
 *
 * `new Date('2026-13-45')` is Invalid Date rather than a throw, and
 * `new Date('2026-02-30')` silently rolls into March, so the parsed value is
 * checked and — for bare dates — round-tripped against the input.
 */
export function parseRequiredIsoDate(value: string, label: string): Date | Refusal {
  const raw = value.trim();
  if (raw === '') return refuse(`${label} is required. Expected ISO 8601, for example "2026-09-01".`);

  // A BARE `YYYY-MM-DD` IS A CIVIL DATE AND MUST NOT BECOME AN INSTANT.
  //
  // `new Date('2026-09-18')` is specified to parse as UTC midnight, but the
  // calendar reads and writes local civil dates. Anywhere west of UTC the
  // round trip loses a day: in America/Chicago, where this client base is,
  // '2026-09-18' comes back as 2026-09-17. An agent asked to move an article
  // to the 18th would silently move it to the 17th.
  //
  // Building from the parts pins it to local midnight, so what the operator
  // sees on the calendar is what the agent asked for.
  if (ISO_DATE.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    const civil = new Date(y, m - 1, d, 0, 0, 0, 0);
    // `new Date(2026, 1, 30)` rolls into March rather than throwing, so a
    // date that does not exist is rejected instead of silently shifted.
    if (civil.getFullYear() !== y || civil.getMonth() + 1 !== m || civil.getDate() !== d) {
      return refuse(`${label} is not a real calendar date. Received ${JSON.stringify(value)}.`);
    }
    return civil;
  }

  // A full instant is an instant; it keeps its offset and is never re-based.
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return refuse(
      `${label} must be a valid ISO 8601 date. Expected "YYYY-MM-DD" or a full instant, for example "2026-09-01" or "2026-09-01T14:30:00Z". Received ${JSON.stringify(value)}.`,
    );
  }
  return parsed;
}

/** `"today"` is a literal, matching big-calendar `:71-78`. */
export function resolveDayRange(value: string): DateRange | Refusal {
  const target = value === 'today' ? new Date() : parseRequiredIsoDate(value, 'date');
  if (isRefusal(target)) return target;

  const start = new Date(target);
  start.setHours(0, 0, 0, 0);
  const end = new Date(target);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/** `YYYY-MM` expanded to an inclusive month end, matching big-calendar `:80-87`. */
export function resolveMonthRange(month: string): DateRange | Refusal {
  if (!ISO_MONTH.test(month.trim())) {
    return refuse(`month must be "YYYY-MM", for example "2026-09". Received ${JSON.stringify(month)}.`);
  }
  const [year, monthIndex] = month.trim().split('-').map(Number);
  return {
    start: new Date(year, monthIndex - 1, 1, 0, 0, 0, 0),
    // Day 0 of the next month is the last day of this one.
    end: new Date(year, monthIndex, 0, 23, 59, 59, 999),
  };
}

/**
 * OVERLAP, not containment — big-calendar `:89-94`. An item counts if either
 * endpoint falls inside the window, or if it straddles the window entirely.
 * Containment silently drops anything spanning a month boundary.
 */
export function overlapsRange(itemStart: Date, itemEnd: Date, range: DateRange): boolean {
  const s = itemStart.getTime();
  const e = itemEnd.getTime();
  const rs = range.start.getTime();
  const re = range.end.getTime();
  return (s >= rs && s <= re) || (e >= rs && e <= re) || (s <= rs && e >= re);
}

/**
 * A window with either endpoint, or both, left open.
 *
 * `null` is ABSENT, never "the beginning of time". The previous shape was a
 * `DateRange` with sentinel endpoints at ±8.64e15, which forced every caller
 * to invent a date it never had: the seam cannot ask the calendar endpoint for
 * `start_date=275760-09-13`, and a bare call cannot be told apart from a
 * caller that genuinely asked for all of history.
 */
export interface ArticleWindow {
  readonly from: Date | null;
  readonly to: Date | null;
}

/**
 * Resolves the `from`/`to`/`month` argument trio into one window.
 *
 * `month` is mutually exclusive with `from`/`to`; big-calendar leaves that
 * conflict implicit and it is made explicit here for the same reason its
 * `navigate` rejects `date` with `eventId` (`:469-501`) — a silently ignored
 * argument is worse than a refusal, because the agent believes it was applied.
 *
 * NOTHING SPECIFIED IS NO DATE FILTER, NOT TODAY.
 *
 * This used to return `resolveDayRange('today')` for the empty trio, so a bare
 * `planner_find_articles({})` — what an agent sends for "what is in the
 * planner" — silently became "articles dated today" and answered "No articles
 * matched" on a workspace holding dozens. The tool describes the window as one
 * filter among several, so its absence has to mean the filter is off.
 *
 * Start-before-end is checked LAST, after both endpoints are known, so a
 * partial update is validated against the value it did not change.
 */
export function resolveArticleWindow(args: {
  readonly from?: string;
  readonly to?: string;
  readonly month?: string;
}): ArticleWindow | Refusal {
  const { from, to, month } = args;

  if (month !== undefined && (from !== undefined || to !== undefined)) {
    return refuse('Provide either month, or from/to, not both.');
  }
  if (month !== undefined) {
    const range = resolveMonthRange(month);
    if (isRefusal(range)) return range;
    return { from: range.start, to: range.end };
  }

  if (from === undefined && to === undefined) return { from: null, to: null };

  const start = from === undefined ? null : parseRequiredIsoDate(from, 'from');
  if (isRefusal(start)) return start;
  const end = to === undefined ? null : parseRequiredIsoDate(to, 'to');
  if (isRefusal(end)) return end;

  if (start !== null && end !== null && start.getTime() > end.getTime()) {
    return refuse('from must be before to.');
  }
  return { from: start, to: end };
}

/** `YYYY-MM-DD` in local time, the shape the calendar display date expects. */
export function toCalendarDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
